import { faSearch } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, CircularProgress, InputAdornment, MenuItem, TextField, Typography } from "@mui/material"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { translate } from "app/language/service"
import { getFormatedNumber } from "app/tools/number"
import { IForm, SELECT_FILTER_VALUES } from "form/model"
import { noticeAndClearDspDealFormatError, synchronizeDspDealFormats } from "form/tools/dsp-deal"
import { IFormat, IFormatFilter } from "format/model"
import { getAllFormatsInStore, getDefaultFormatFilter, getFilteredFormats } from "format/tools/common"
import React from "react"
import { FlexBox } from "ui/components/FlexBox"
import { ListIdCell } from "ui/components/ListIdCell"
import { getDataGridTexts } from "ui/tools/list"
import { SelectionFilter } from "../filter/SelectionFilter"

interface ICreativeDspDealBoxProps {
  form: IForm
}

export const CreativeDspDealBox = React.memo(({ form }: ICreativeDspDealBoxProps): JSX.Element => {
  const [selectedIds, setSelectedIds] = React.useState<number[]>(form.configuration.dspDeal.formatIds)
  const [filter, setFilter] = React.useState<IFormatFilter>(getDefaultFormatFilter())
  const isNoticingError = React.useRef(false)
  const canClear = !(!filter.heigth && !filter.width && !filter.name && !filter.orientation && filter.selected === null)
  React.useEffect(() => {
    if (form.context.relations.dspDealFormatsError) {
      if (!isNoticingError.current) {
        isNoticingError.current = true
        noticeAndClearDspDealFormatError(form)
        setSelectedIds(form.configuration.dspDeal.formatIds)
      }
    } else if (isNoticingError.current) {
      isNoticingError.current = false
    }
  })
  const filteredFormats = getFilteredFormats(filter, selectedIds)
  const columns: Array<GridColDef<IFormat>> = [
    {
      field: "name",
      headerName: translate("label.name"),
      flex: 3,
      renderCell: params => <ListIdCell label={params.row.name || "-"} id={params.row.id} />
    },
    {
      field: "width",
      headerName: translate("label.width"),
      renderCell: params => getFormatedNumber(params.row.width),
      flex: 4
    },
    {
      field: "height",
      headerName: translate("label.height"),
      renderCell: params => getFormatedNumber(params.row.height),
      flex: 4
    },
    {
      field: "orientation",
      headerName: translate("label.orientation"),
      flex: 2,
      renderCell: params => (params.row.orientation ? translate(`label.orientation.${params.row.orientation}`) : "-")
    }
  ]
  return (
    <FlexBox
      id="form-dsp-media-format"
      vertical
      ai="start"
      jc="start"
      gap
      fullWidth
      sx={{
        "p": "1rem",
        "pt": 0,
        "height": "100%",
        "& > *": {
          width: "100%"
        },
        "& .MuiDataGrid-selectedRowCount": {
          visibility: "hidden"
        },
        "& .MuiDataGrid-cell": {
          fontSize: ".8rem",
          fontWeight: 300
        },
        "& .MuiDataGrid-row": {
          cursor: "pointer"
        },
        "& .MuiDataGrid-row.Mui-selected": {
          backgroundColor: "inherit"
        },
        "& .MuiDataGrid-columnHeader, & .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell":
          {
            outline: "none !important"
          }
      }}>
      <FlexBox gap vertical fullWidth ai="start" sx={{ mt: "1rem" }}>
        {form.configuration.dspDeal.isSynchronizingFormat ? (
          <CircularProgress id="form-dsp-media-format-is-synchronizing" size="1.5rem" />
        ) : selectedIds.length === 0 ? (
          <Typography id="form-dsp-media-format-empty-message" color="textSecondary" fontSize=".85rem">
            {translate("dsp-deal.format.help")}
          </Typography>
        ) : (
          <Typography color="textSecondary" id="form-dsp-media-format-selection-message" fontSize=".85rem">
            {translate("dsp-deal.format.selected", `${selectedIds.length}/${getAllFormatsInStore().length}`)}
          </Typography>
        )}
        <FlexBox jc="start" gap wrap>
          <TextField
            id="form-dsp-media-format-filter-name"
            label={translate("label.filter.name")}
            value={filter.name}
            onChange={event => setFilter({ ...filter, name: event.target.value })}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FontAwesomeIcon icon={faSearch} size="lg" />
                </InputAdornment>
              )
            }}
          />
          <TextField
            id="form-dsp-media-format-filter-width"
            label={translate("label.width")}
            value={filter.width || ""}
            type="number"
            size="small"
            sx={{ maxWidth: "8rem" }}
            onChange={event => setFilter({ ...filter, width: Number(event.target.value) || null })}
          />
          <TextField
            id="form-dsp-media-format-filter-height"
            label={translate("label.height")}
            value={filter.heigth || ""}
            type="number"
            size="small"
            sx={{ maxWidth: "8rem" }}
            onChange={event => setFilter({ ...filter, heigth: Number(event.target.value) || null })}
          />
          <TextField
            id="form-dsp-media-format-filter-orientation"
            variant="outlined"
            select
            size="small"
            sx={{ minWidth: "8rem" }}
            label={translate("label.orientation")}
            value={filter.orientation}
            onChange={event => setFilter({ ...filter, orientation: (event.target.value as any) || null })}>
            <MenuItem id="form-dsp-media-format-filter-orientation-option-0" value={""}>
              -
            </MenuItem>
            <MenuItem id="form-dsp-media-format-filter-orientation-option-1" value={"vertical"}>
              {translate("label.orientation.vertical")}
            </MenuItem>
            <MenuItem id="form-dsp-media-format-filter-orientation-option-2" value={"horizontal"}>
              {translate("label.orientation.horizontal")}
            </MenuItem>
            <MenuItem id="form-dsp-media-format-filter-orientation-option-3" value={"both"}>
              {translate("label.orientation.both")}
            </MenuItem>
          </TextField>
          <SelectionFilter
            id="form-dsp-media-format-filter-selection"
            value={
              filter.selected === null
                ? SELECT_FILTER_VALUES.ALL
                : filter.selected === true
                  ? SELECT_FILTER_VALUES.SELECTED
                  : SELECT_FILTER_VALUES.NOT_SELECTED
            }
            onChange={newSelectFilter =>
              setFilter({
                ...filter,
                selected:
                  newSelectFilter === SELECT_FILTER_VALUES.ALL
                    ? null
                    : newSelectFilter === SELECT_FILTER_VALUES.SELECTED
                      ? true
                      : false
              })
            }
          />
          <Button
            id="form-dsp-media-format-clear-filter-button"
            variant="text"
            disabled={!canClear}
            onClick={event => {
              event.stopPropagation()
              if (!canClear) {
                return
              }
              setFilter(getDefaultFormatFilter())
            }}>
            {translate("label.clear.filters")}
          </Button>
        </FlexBox>
      </FlexBox>
      <DataGrid
        rows={filteredFormats}
        checkboxSelection
        columns={columns}
        disableColumnFilter
        disableColumnMenu
        selectionModel={selectedIds}
        keepNonExistentRowsSelected
        initialState={{
          sorting: { sortModel: [{ field: "name", sort: "asc" }] }
        }}
        rowHeight={40}
        autoPageSize
        onSelectionModelChange={newSelectedIds => {
          synchronizeDspDealFormats(form, newSelectedIds as number[])
          setSelectedIds(newSelectedIds as number[])
        }}
        localeText={getDataGridTexts()}
      />
    </FlexBox>
  )
})
